import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Tabs, Tab, useTheme, useMediaQuery, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, IconButton, AppBar, Toolbar, Typography, Switch, Menu, MenuItem, ListItemIcon, ListItemText, TextField } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import BarChartIcon from '@mui/icons-material/BarChart';
import SettingsIcon from '@mui/icons-material/Settings';
import FlagIcon from '@mui/icons-material/Flag';
import GroupIcon from '@mui/icons-material/Group';
import HelpIcon from '@mui/icons-material/Help';
import ListAltIcon from '@mui/icons-material/ListAlt';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import NotesIcon from '@mui/icons-material/Notes';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import YearTabs from './YearTabs';
import Statistics from './Statistics';
import HabitManagement from './HabitManagement';
import Goals from './Goals';
import UserManagement from './UserManagement';
import Help from './Help';
import Settings from './Settings';
import { getContrastText } from '../utils/colorUtils';
import { alpha } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, updateDoc, onSnapshot, arrayRemove, arrayUnion } from 'firebase/firestore';
import { db, isUserAdmin } from '../services/firebase';
import MonthCalendar from './MonthCalendar';
import Challenges from './Challenges';
import Notes from './Notes';
import MenuComponent from './Menu';
import { useNavigate, Navigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import logo from '../assets/logo.png';
import Productivity from './Productivity';
import LogoutIcon from '@mui/icons-material/Logout';
import Calendar from './Calendar';
import PageLayout from './PageLayout';
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement';
import Meditations from './Meditations';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import HomeIcon from '@mui/icons-material/Home';
import SpaIcon from '@mui/icons-material/Spa';
import { Link } from 'react-router-dom';
import { usePlayer } from '../contexts/PlayerContext';
import TimerIcon from '@mui/icons-material/Timer';
import EventIcon from '@mui/icons-material/Event';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NoteIcon from '@mui/icons-material/Note';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import BookRecommendations from './BookRecommendations';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import ptBR from 'date-fns/locale/pt-BR';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const isModuleEnabled = (moduleId) => {
  // Sempre habilita Menu, Settings e Help
  if (moduleId === 'menu' || moduleId === 'settings' || moduleId === 'help') {
    return true;
  }
  return true; // Por enquanto, todos os módulos estão habilitados
};

const menuItems = [
  { id: 'menu', label: 'Menu', icon: <HomeIcon /> },
  { id: 'calendar', label: 'Calendário', icon: <CalendarMonthIcon /> },
  { id: 'habits', label: 'Hábitos', icon: <CheckCircleIcon /> },
  { id: 'goals', label: 'Metas', icon: <FlagIcon /> },
  { id: 'challenges', label: 'Desafios', icon: <EmojiEventsIcon /> },
  { id: 'productivity', label: 'Produtividade', icon: <TimerIcon /> },
  { id: 'notes', label: 'Notas', icon: <NoteIcon /> },
  { id: 'events', label: 'Eventos', icon: <EventIcon /> },
  { id: 'statistics', label: 'Estatísticas', icon: <BarChartIcon /> },
  { id: 'livros', label: ' Dicas de Livros', icon: <MenuBookIcon /> },
  { id: 'settings', label: 'Configurações', icon: <SettingsIcon /> },
  { id: 'help', label: 'Ajuda', icon: <HelpIcon /> }
];

const availableModules = menuItems.filter(item => {
  // Sempre mostra Menu, Configurações e Ajuda
  if (item.id === 'menu' || item.id === 'settings' || item.id === 'help') return true;
  // Para os outros módulos, verifica se estão habilitados
  return isModuleEnabled(item.id);
});

function Navigation({ 
  habits, 
  onAddHabit, 
  onDeleteHabit,
  onDeleteAllHabits,
  onUpdateHabit,
  onToggleHabit,
  onReorderHabits,
  customColors,
  onUpdateCustomColors,
  showDailyQuote,
  onToggleDailyQuote,
  userId,
  mode,
  onToggleMode,
  currentTab,
  setCurrentTab,
  children
}) {
  const [value, setValue] = useState(0);
  const [calendarComponentsOrder, setCalendarComponentsOrder] = useState(['calendar', 'progress', 'donuts', 'area', 'stats']);
  const [isAdmin, setIsAdmin] = useState(false);
  const [enabledModules, setEnabledModules] = useState({});
  const [preferences, setPreferences] = useState(null);
  const [goals, setGoals] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { currentUser, logout } = useAuth();
  const { handleStop } = usePlayer();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [habitToDelete, setHabitToDelete] = useState(null);
  const [showDeleteAllDialog, setShowDeleteAllDialog] = useState(false);
  const [showDeleteAllGoalsDialog, setShowDeleteAllGoalsDialog] = useState(false);
  const [showDeleteAllChallengesDialog, setShowDeleteAllChallengesDialog] = useState(false);
  const [showDeleteAllNotesDialog, setShowDeleteAllNotesDialog] = useState(false);
  const navigate = useNavigate();
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const habitManagementRef = useRef();
  const [anchorEl, setAnchorEl] = useState(null);
  const [editEventDialog, setEditEventDialog] = useState({ open: false, event: null });
  const [editEventForm, setEditEventForm] = useState(null);
  const [editEventError, setEditEventError] = useState('');
  const [events, setEvents] = useState([]);
  const [successSnackbar, setSuccessSnackbar] = useState(false);

  const checkModuleEnabled = useCallback((moduleName) => {
    return true; // Todos os módulos estão habilitados
  }, []);

  const handleMonthChange = (month) => {
    setSelectedMonth(month);
  };

  const handleYearChange = (year) => {
    setSelectedYear(year);
  };

  const handleLogout = async () => {
    try {
      // Para o áudio antes de fazer logout
      handleStop();
      
      await logout();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  };

  const handleTabChange = (newTab) => {
    setCurrentTab(newTab);
    navigate(`/${newTab === 'menu' ? '' : newTab}`);
  };

  useEffect(() => {
    const loadComponentsOrder = async () => {
      if (userId) {
        const userRef = doc(db, 'users', userId);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const userData = userSnap.data();
          if (userData.preferences?.calendarComponentsOrder) {
            setCalendarComponentsOrder(userData.preferences.calendarComponentsOrder);
          }
        }
      }
    };
    loadComponentsOrder();
  }, [userId]);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (userId) {
        try {
          const userRef = doc(db, 'users', userId);
          const userSnap = await getDoc(userRef);
          
          if (userSnap.exists()) {
            const userData = userSnap.data();
          } else {
            console.error('Documento do usuário não encontrado no Navigation');
          }
          
          const adminStatus = await isUserAdmin(userId);
          setIsAdmin(adminStatus);
        } catch (error) {
          console.error('Erro ao verificar status de admin:', error);
          setIsAdmin(false);
        }
      }
    };
    
    checkAdminStatus();
  }, [userId]);

  useEffect(() => {
    const loadUserModules = async () => {
      if (userId) {
        try {
          const userRef = doc(db, 'users', userId);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            const userData = userSnap.data();
            setEnabledModules(userData.enabledModules || availableModules.map(m => m.id));
          }
        } catch (error) {
          console.error('Erro ao carregar módulos:', error);
        }
      }
    };
    loadUserModules();
  }, [userId]);

  useEffect(() => {
    if (!userId) return;

    // Carregar metas
    const unsubscribeGoals = onSnapshot(doc(db, 'users', userId), (doc) => {
      if (doc.exists()) {
        const userData = doc.data();
        setGoals(userData.goals || []);
      }
    });

    // Carregar desafios
    const unsubscribeChallenges = onSnapshot(doc(db, 'users', userId), (doc) => {
      if (doc.exists()) {
        const userData = doc.data();
        setChallenges(userData.challenges || []);
      }
    });

    return () => {
      unsubscribeGoals();
      unsubscribeChallenges();
    };
  }, [userId]);

  useEffect(() => {
    const loadUserPreferences = async () => {
      if (userId) {
        try {
          const userRef = doc(db, 'users', userId);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            const userData = userSnap.data();
            setPreferences(userData.preferences || {});
          }
        } catch (error) {
          console.error('Erro ao carregar preferências:', error);
        }
      }
    };
    loadUserPreferences();
  }, [userId]);

  useEffect(() => {
    if (preferences) {
      setEnabledModules({
        habits: true, // Módulo principal sempre habilitado
        calendar: true,
        events: preferences.enableEvents ?? true,
        quotes: preferences.enableQuotes ?? true,
        statistics: preferences.enableStatistics ?? true,
        productivity: true,
        goals: true,
        challenges: true,
        notes: true,
        meditations: true,
        menu: true,
        settings: true,
        help: true
      });
    } else {
      // Define todos os módulos como habilitados por padrão
      setEnabledModules({
        habits: true,
        calendar: true,
        events: true,
        quotes: true,
        statistics: true,
        productivity: true,
        goals: true,
        challenges: true,
        notes: true,
        meditations: true,
        menu: true,
        settings: true,
        help: true
      });
    }
  }, [preferences]);

  useEffect(() => {
    if (editEventDialog.open && editEventDialog.event) {
      setEditEventForm({ ...editEventDialog.event });
      setEditEventError('');
    }
  }, [editEventDialog]);

  const loadEvents = async () => {
    if (!currentUser?.uid) return;
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const userData = userSnap.data();
        const loadedEvents = (userData.events || []).map(event => ({
          ...event,
          date: event.date ? new Date(event.date) : null,
          time: event.time ? new Date(event.time) : null
        }));
        setEvents(loadedEvents);
      }
    } catch (error) {
      // Silenciar erro
    }
  };

  useEffect(() => { loadEvents(); }, [currentUser]);

  const handleEditEventSave = async () => {
    if (!editEventForm.title?.trim()) {
      setEditEventError('O título do evento é obrigatório');
      return;
    }
    if (!editEventForm.date) {
      setEditEventError('A data é obrigatória');
      return;
    }
    if (!currentUser?.uid) {
      setEditEventError('Usuário não autenticado');
      return;
    }
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();
      const events = userData.events || [];
      const eventIndex = events.findIndex(e => e.id === editEventForm.id);
      if (eventIndex !== -1) {
        const updatedEvent = {
          ...events[eventIndex],
          title: editEventForm.title,
          description: editEventForm.description,
          date: editEventForm.date instanceof Date && !isNaN(editEventForm.date)
            ? editEventForm.date.toISOString()
            : (typeof editEventForm.date === 'string' ? editEventForm.date : null),
          time: editEventForm.time instanceof Date && !isNaN(editEventForm.time)
            ? editEventForm.time.toISOString()
            : (typeof editEventForm.time === 'string' ? editEventForm.time : null),
          completed: events[eventIndex].completed,
          completedAt: events[eventIndex].completedAt
        };
        await updateDoc(userRef, {
          events: arrayRemove(events[eventIndex])
        });
        await updateDoc(userRef, {
          events: arrayUnion(updatedEvent)
        });
      }
      await loadEvents();
      setEditEventDialog({ open: false, event: null });
      setSuccessSnackbar(true);
    } catch (error) {
      setEditEventError('Erro ao salvar evento. Tente novamente.');
    }
  };

  const renderContent = () => {
    switch (currentTab) {
      case 'menu':
        return (
          <MenuComponent
            onNavigate={handleTabChange}
            customColors={customColors}
          />
        );
      case 'calendar':
        return checkModuleEnabled('calendar') ? (
          <PageLayout
            title="Calendário"
            hideAddButton
            customColors={customColors}
          >
            <MonthCalendar
              habits={habits}
              month={selectedMonth}
              year={selectedYear}
              onToggleHabit={onToggleHabit}
              onUpdateHabit={onUpdateHabit}
              customColors={customColors}
              showDailyQuote={showDailyQuote}
              onToggleDailyQuote={onToggleDailyQuote}
              componentsOrder={calendarComponentsOrder}
              onReorderComponents={setCalendarComponentsOrder}
              onMonthChange={handleMonthChange}
              onYearChange={handleYearChange}
              onUpdateCustomColors={onUpdateCustomColors}
              onEditEvent={event => setEditEventDialog({ open: true, event })}
              events={events}
              setEvents={setEvents}
            />
            <Dialog open={editEventDialog.open} onClose={() => setEditEventDialog({ open: false, event: null })} maxWidth="sm" fullWidth>
              <DialogTitle>Editar Evento</DialogTitle>
              <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                  <TextField
                    label="Título"
                    fullWidth
                    value={editEventForm?.title || ''}
                    onChange={e => setEditEventForm(prev => ({ ...prev, title: e.target.value }))}
                    required
                    error={!editEventForm?.title}
                    helperText={!editEventForm?.title ? "Título é obrigatório" : ""}
                  />
                  <TextField
                    label="Descrição"
                    fullWidth
                    multiline
                    rows={4}
                    value={editEventForm?.description || ''}
                    onChange={e => setEditEventForm(prev => ({ ...prev, description: e.target.value }))}
                  />
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                    <DatePicker
                      label="Data"
                      value={editEventForm?.date || null}
                      onChange={date => setEditEventForm(prev => ({ ...prev, date }))}
                      renderInput={params => (
                        <TextField 
                          {...params} 
                          fullWidth 
                          required
                          error={!editEventForm?.date}
                          helperText={!editEventForm?.date ? "Data é obrigatória" : ""}
                        />
                      )}
                    />
                    <TimePicker
                      label="Horário (opcional)"
                      value={editEventForm?.time || null}
                      onChange={time => setEditEventForm(prev => ({ ...prev, time }))}
                      renderInput={params => <TextField {...params} fullWidth />}
                    />
                  </LocalizationProvider>
                  {editEventError && (
                    <Typography color="error" variant="body2">{editEventError}</Typography>
                  )}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setEditEventDialog({ open: false, event: null })}>Cancelar</Button>
                <Button 
                  onClick={handleEditEventSave}
                  variant="contained"
                  disabled={!editEventForm?.title || !editEventForm?.date}
                  color="primary"
                >
                  Salvar
                </Button>
              </DialogActions>
            </Dialog>
          </PageLayout>
        ) : <Navigate to="/menu" replace />;
      case 'habits':
        return (
          <PageLayout
            title="Hábitos"
            customColors={customColors}
            onAdd={handleAddClick}
            actionButtons={
              <Box sx={{ 
                display: 'flex', 
                gap: 1,
                width: { xs: '100%', sm: 'auto' },
                '& .MuiButton-root': {
                  flex: { xs: 1, sm: 'initial' },
                  minWidth: { xs: 0, sm: 'auto' },
                  px: { xs: 1.5, sm: 3 },
                  whiteSpace: 'nowrap',
                  fontSize: { xs: '0.75rem', sm: '1rem' }
                }
              }}>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => {
                    setShowDeleteAllDialog(true);
                  }}
                >
                  Remover Todos
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddClick}
                  sx={{
                    bgcolor: customColors?.primary || 'primary.main',
                    color: customColors?.primary ? getContrastText(customColors.primary) : 'white',
                    '&:hover': {
                      bgcolor: customColors?.primary || 'primary.main',
                      opacity: 0.9
                    }
                  }}
                >
                  Novo Hábito
                </Button>
              </Box>
            }
          >
            <HabitManagement
              ref={habitManagementRef}
              habits={habits}
              onDeleteHabit={onDeleteHabit}
              onUpdateHabit={onUpdateHabit}
              onReorderHabits={onReorderHabits}
              onAddHabit={onAddHabit}
              onDeleteAllHabits={onDeleteAllHabits}
              customColors={customColors}
            />
          </PageLayout>
        );
      case 'goals':
        return (
          <Goals
            habits={habits}
            customColors={customColors}
            onAdd={handleOpenDialog}
          />
        );
      case 'challenges':
        return (
          <Challenges
            customColors={customColors}
            userId={userId}
            onAdd={handleOpen}
          />
        );
      case 'productivity':
        return checkModuleEnabled('productivity') ? (
          <Productivity customColors={customColors} />
        ) : <Navigate to="/menu" replace />;
      case 'statistics':
        return (
          <PageLayout
            title="Estatísticas"
            hideAddButton
            customColors={customColors}
            actionButtons={<Box sx={{ minHeight: 0, height: 0, p: 0, m: 0 }} />}
            hideActionHeader={true}
          >
            <Statistics
              habits={habits}
              goals={goals}
              challenges={challenges}
              customColors={customColors}
            />
          </PageLayout>
        );
      case 'notes':
        return (
          <Notes
            customColors={customColors}
            onAdd={handleOpenDialog}
          />
        );
        
      case 'settings':
        return (
          <Settings
            customColors={customColors}
            onUpdateCustomColors={onUpdateCustomColors}
            showDailyQuote={showDailyQuote}
            onToggleDailyQuote={onToggleDailyQuote}
            mode={mode}
            onToggleMode={onToggleMode}
          />
        );
      case 'help':
        return (
          <Help
            customColors={customColors}
          />
        );
      case 'userManagement':
        return isAdmin ? (
          <UserManagement />
        ) : null;
      case 'meditations':
        return checkModuleEnabled('meditations') ? (
          <Meditations customColors={customColors} />
        ) : <Navigate to="/menu" replace />;
      case 'livros':
        return (
          <PageLayout
            title="Livros Recomendados"
            customColors={customColors}
          >
            <BookRecommendations />
          </PageLayout>
        );
      case 'bookRecommendations':
        return (
          <PageLayout
            title="Dicas de Livros"
            customColors={customColors}
          >
            <BookRecommendations />
          </PageLayout>
        );
      default:
        return null;
    }
  };

  const handleDeleteClick = (habit) => {
    setHabitToDelete(habit);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = () => {
    onDeleteHabit(habitToDelete);
    setShowDeleteDialog(false);
  };

  const handleDeleteAllClick = () => {
    setShowDeleteAllDialog(true);
  };

  const handleDeleteAllConfirm = () => {
    onDeleteAllHabits();
    setShowDeleteAllDialog(false);
  };

  const handleBackClick = () => {
    setCurrentTab('menu');
  };

  const handleDeleteAllGoals = () => {
    setShowDeleteAllGoalsDialog(true);
  };

  const handleDeleteAllGoalsConfirm = async () => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, { goals: [] });
      setShowDeleteAllGoalsDialog(false);
    } catch (error) {
      console.error('Erro ao excluir todas as metas:', error);
    }
  };

  const handleOpenDialog = () => {
    if (currentTab === 'goals') {
      Goals.onAdd();
    } else if (currentTab === 'notes') {
      Notes.onAdd();
    }
  };

  const handleOpen = () => {
    if (currentTab === 'challenges') {
      Challenges.onAdd();
    }
  };

  const handleAddClick = () => {
    switch (currentTab) {
      case 'habits':
        if (habitManagementRef.current) {
          habitManagementRef.current.handleAddClick();
        }
        break;
      case 'goals':
        if (typeof Goals.onAdd === 'function') {
          Goals.onAdd();
        }
        break;
      case 'challenges':
        if (typeof Challenges.onAdd === 'function') {
          Challenges.onAdd();
        }
        break;
      case 'notes':
        if (typeof Notes.onAdd === 'function') {
          Notes.onAdd();
        }
        break;
    }
  };

  const handleDeleteAllChallenges = () => {
    setShowDeleteAllChallengesDialog(true);
  };

  const handleDeleteAllChallengesConfirm = async () => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, { challenges: [] });
      setShowDeleteAllChallengesDialog(false);
    } catch (error) {
      console.error('Erro ao excluir todos os desafios:', error);
    }
  };

  const handleDeleteAllNotes = () => {
    setShowDeleteAllNotesDialog(true);
  };

  const handleDeleteAllNotesConfirm = async () => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, { notes: [] });
      setShowDeleteAllNotesDialog(false);
    } catch (error) {
      console.error('Erro ao excluir todas as notas:', error);
    }
  };

  const handleHighlightTodayToggle = () => {
    // Implementation of handleHighlightTodayToggle
  };

  const handleHighlightPerfectDayToggle = () => {
    // Implementation of handleHighlightPerfectDayToggle
  };

  const handleShowDailyQuoteToggle = () => {
    // Implementation of handleShowDailyQuoteToggle
  };

  const handleShowProgrammedDaysToggle = () => {
    // Implementation of handleShowProgrammedDaysToggle
  };

  const handleModeChange = () => {
    onToggleMode();
  };

  const handleMenuClick = (path) => {
    const tab = path.replace('/', '');
    handleTabChange(tab || 'menu');
    setAnchorEl(null); // Fecha o menu móvel
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: customColors?.primary || 'primary.main'
        }}
      >
        <Toolbar>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            maxWidth: 'auto',
            gap: 2
          }}>
            {isMobile ? (
              <>
                <IconButton
                  onClick={handleMenuOpen}
                  sx={{
                    color: customColors?.primary ? getContrastText(customColors.primary) : 'white'
                  }}
                >
                  <MenuIcon />
                </IconButton>
                <Typography
                  variant="h6"
                  component="div"
                  sx={{
                    flexGrow: 1,
                    textAlign: 'center',
                    color: customColors?.primary ? getContrastText(customColors.primary) : 'white',
                    fontSize: { xs: '1.1rem', sm: '1.25rem' },
                    mx: { xs: 1 },
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {menuItems.find(item => item.id === currentTab)?.label || 'Menu'}
                </Typography>
              </>
            ) : (
              <>
                {menuItems.map((item) => (
                  <Button
                    key={item.id}
                    onClick={() => handleTabChange(item.id)}
                    sx={{
                      color: customColors?.primary ? getContrastText(customColors.primary) : 'white',
                      bgcolor: currentTab === item.id ? alpha(customColors?.primary ? getContrastText(customColors.primary) : 'white', 0.1) : 'transparent',
                      '&:hover': {
                        bgcolor: alpha(customColors?.primary ? getContrastText(customColors.primary) : 'white', 0.2)
                      },
                      mx: 0.5
                    }}
                    startIcon={item.icon}
                  >
                    {item.label}
                  </Button>
                ))}
              </>
            )}
            <IconButton 
              onClick={handleModeChange}
              sx={{
                color: customColors?.primary ? getContrastText(customColors.primary) : 'white'
              }}
            >
              {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: '100%',
          minHeight: '100vh',
          pt: { xs: 8, sm: 9 },
          bgcolor: theme.palette.background.default
        }}
      >
        {children || renderContent()}
      </Box>

      {/* Diálogo de confirmação para remover todos os hábitos */}
      <Dialog
        open={showDeleteAllDialog}
        onClose={() => setShowDeleteAllDialog(false)}
        aria-labelledby="delete-all-dialog-title"
        aria-describedby="delete-all-dialog-description"
      >
        <DialogTitle id="delete-all-dialog-title">Confirmar exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-all-dialog-description">
            Tem certeza que deseja excluir todos os hábitos? Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteAllDialog(false)}>Cancelar</Button>
          <Button 
            onClick={() => {
              onDeleteAllHabits();
              setShowDeleteAllDialog(false);
            }} 
            color="error" 
            variant="contained"
          >
            Excluir Todos
          </Button>
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {menuItems.map((item) => (
          <MenuItem key={item.id} onClick={() => handleMenuClick(`/${item.id === 'menu' ? '' : item.id}`)}>
            <ListItemIcon>
              {item.icon}
            </ListItemIcon>
            <ListItemText>{item.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>

      <Snackbar open={successSnackbar} autoHideDuration={3000} onClose={() => setSuccessSnackbar(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <MuiAlert onClose={() => setSuccessSnackbar(false)} severity="success" elevation={6} variant="filled">
          Alteração efetuada com sucesso
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}

export default Navigation; 